import { sha256 as jsSha256 } from 'js-sha256'
import SparkMD5 from 'spark-md5'

export interface DirectUploadResponse {
  blob_signed_id: string
  direct_upload: {
    url: string
    headers: Record<string, string>
  }
  max_byte_size: number
}

export interface FileChecksums {
  byteSize: number
  contentType: string
  sha256Hex: string
  activeStorageChecksum: string
}

const CHUNK_SIZE = 8 * 1024 * 1024 // 8 MB

function rawToBase64(raw: string): string {
  return btoa(raw)
}

export async function computeFileChecksums(file: File): Promise<FileChecksums> {
  const sha256Hasher = jsSha256.create()
  const md5Hasher = new SparkMD5.ArrayBuffer()

  for (let offset = 0; offset < file.size; offset += CHUNK_SIZE) {
    const chunk = await file.slice(offset, offset + CHUNK_SIZE).arrayBuffer()
    sha256Hasher.update(chunk)
    md5Hasher.append(chunk)
  }

  return {
    byteSize: file.size,
    contentType: file.type || 'application/octet-stream',
    sha256Hex: sha256Hasher.hex(),
    activeStorageChecksum: rawToBase64(md5Hasher.end(true)),
  }
}

function csrfToken(): string | undefined {
  return document
    .querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
    ?.content
}

export async function requestDirectUpload(
  url: string,
  body: Record<string, unknown>,
): Promise<DirectUploadResponse> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }

  const token = csrfToken()
  if (token)
    headers['X-CSRF-Token'] = token

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    credentials: 'same-origin',
  })

  const payload = await response.json().catch(() => ({}))
  if (!response.ok)
    throw new Error(payload.error ?? 'Could not start the upload')

  return payload as DirectUploadResponse
}

export function uploadFileBytes(
  upload: DirectUploadResponse['direct_upload'],
  file: File,
  onProgress: (loaded: number, total: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest()
    request.open('PUT', upload.url, true)

    for (const [name, value] of Object.entries(upload.headers))
      request.setRequestHeader(name, value)

    request.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable)
        onProgress(event.loaded, event.total)
    })

    request.addEventListener('load', () => {
      if (request.status >= 200 && request.status < 300)
        resolve()
      else
        reject(new Error('The file could not be uploaded to storage'))
    })

    request.addEventListener('error', () => reject(new Error('The file could not be uploaded to storage')))
    request.addEventListener('abort', () => reject(new Error('The upload was cancelled')))

    request.send(file)
  })
}
